import { useState } from "react";
import { cn } from "@/lib/utils";
import { AgeSurchargeIndicator } from "@/app/Common/Components/AgeSurchargeIndicator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Separator } from "@/components/ui/separator";
import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";

import { VehicleIcon } from "@/app/Vehicles/Components/VehicleIcon";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { VehicleType } from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useNavigate } from "@tanstack/react-router";
import { BookingChangeVehicleDialog } from "@/app/Bookings/Components/Dialogs/BookingChangeVehicleDialog";
import { Button } from "@/components/catalyst/button";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateBookingMutation } from "@/app/Bookings/GraphQL/bookingMutations";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid";

export function BookingVehicleCard() {
  const t = useTranslations("vehicle");
  const tCard = useTranslations("booking.cards.vehicleCard");
  const booking = useBookingRecord();
  const { open: changeVehicle } = useDialog(BookingChangeVehicleDialog);
  const navigate = useNavigate();
  const vehicle = booking.vehicle;
  const isSleeper = [
    VehicleType.FourWheelDriveCamper,
    VehicleType.CamperVan,
    VehicleType.MotorHome,
  ].includes(vehicle.type);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/50 flex flex-row items-center">
        <div className="mr-2">
          <VehicleIcon model={vehicle} className="h-12 w-12" />
        </div>
        <div className="grid gap-0.5">
          <CardTitle className="group flex items-center gap-2 text-lg">
            {booking.vehicle.name}
          </CardTitle>
          {vehicle.code ? (
            <CardDescription>
              {tCard("code", { code: vehicle.code })}
            </CardDescription>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            outline
            onClick={() => {
              return navigate({
                to: ".",
                search: (prev: any) => ({
                  ...prev,
                  vehicleId: vehicle.id,
                }),
              });
            }}
          >
            {tCard("editVehicle")}
          </Button>
          <Button
            outline
            onClick={() => {
              changeVehicle({ bookingId: booking.id });
            }}
          >
            {tCard("changeVehicle")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 text-sm">
        <div className="grid gap-3">
          <div className="font-semibold">{tCard("detailsTitle")}</div>
          <dl className="grid gap-3">
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.brand")}</dt>
              <dd>{vehicle.brand}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.model")}</dt>
              <dd>{vehicle.model}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{tCard("category")}</dt>
              <dd>{t(`type.${vehicle.type}`)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.transmission")}</dt>
              <dd>{t(`transmission.${vehicle.transmission}`)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.fuel")}</dt>
              <dd>{t(`fuel.${vehicle.fuel}`)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{tCard("age")}</dt>
              <dd>
                {vehicle.approximate_age
                  ? t(`approximate_age.${vehicle.approximate_age}`)
                  : "--"}
              </dd>
            </div>
          </dl>
        </div>
        <Separator className="my-4" />
        <div className="grid gap-3">
          <div className="font-semibold">{tCard("seatingTitle")}</div>
          <dl className="grid gap-3">
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.seatbelts")}</dt>
              <dd>{vehicle.seatbelts}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("form.child_seat_anchors")}</dt>
              <dd>{vehicle.child_seat_anchor_points ?? "--"}</dd>
            </div>
            {isSleeper ? (
              <div className="flex items-center justify-between">
                <dt className="text-gray-500">{t("form.sleeps")}</dt>
                <dd>{vehicle.sleeps ?? "--"}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        {isSleeper ? (
          <>
            <Separator className="my-4" />
            <div className="grid gap-3">
              <div className="font-semibold">{tCard("featuresTitle")}</div>
              <dl className="grid grid-cols-4 gap-3">
                <div className="flex items-center">
                  <dt className="text-gray-500">{t("form.heating")}:&nbsp;</dt>
                  <dd>
                    <InclusionIcon included={vehicle.has_heating} />
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500">{t("form.kitchen")}:&nbsp;</dt>
                  <dd>
                    <InclusionIcon included={vehicle.has_kitchen} />
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500">
                    {t("form.shower_and_toilet")}:&nbsp;
                  </dt>
                  <dd>
                    <InclusionIcon included={vehicle.has_shower_and_toilet} />
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500">
                    {t("form.self_contained")}:&nbsp;
                  </dt>
                  <dd>
                    <InclusionIcon included={vehicle.is_self_contained} />
                  </dd>
                </div>
              </dl>
            </div>
          </>
        ) : null}
        {booking.supplier.has_age_surcharge ? (
          <>
            <Separator className="my-4" />
            <div className="grid gap-3">
              <div className="font-semibold">
                {tCard("ageRequirementsTitle")}
              </div>
              <AgeSurchargeIndicator
                minimumAge={booking.supplier.minimum_age}
                maximumAge={booking.supplier.maximum_age}
                minimumNoSurchargeAge={
                  booking.supplier.minimum_no_surcharge_age ?? 0
                }
                maximumNoSurchargeAge={
                  booking.supplier.maximum_no_surcharge_age ?? 0
                }
                ageSurchargeAmount={booking.supplier.age_surcharge_amount ?? 0}
                ageSurchargeType={booking.supplier.age_surcharge_type}
                currency={booking.currency}
              />
            </div>
          </>
        ) : null}
        <Separator className="my-4" />
        <BookingExtrasSection booking={booking} />
      </CardContent>
    </Card>
  );
}

function BookingExtrasSection({ booking }: { booking: BookingRecord }) {
  const t = useTranslations("booking.cards.extrasCard");
  const { mutateAsync, isPending } = useGqlMutation(updateBookingMutation);
  const [draftQuantities, setDraftQuantities] = useState<
    Record<string, number>
  >({});

  const offeredExtras = booking.availablePurchasableExtras;
  const bookedOnlyExtras = booking.purchasableExtras
    .map((extra) => extra.supplierPurchasableExtra)
    .filter((extra) => !offeredExtras.some(({ id }) => id === extra.id));
  const extras = [...offeredExtras, ...bookedOnlyExtras];

  const findBookingExtra = (supplierExtraId: string) =>
    booking.purchasableExtras.find(
      (extra) => extra.supplierPurchasableExtra.id === supplierExtraId,
    );
  const savedQuantity = (supplierExtraId: string) =>
    findBookingExtra(supplierExtraId)?.quantity ?? 0;

  const changes = Object.entries(draftQuantities).filter(
    ([supplierExtraId, quantity]) =>
      quantity !== savedQuantity(supplierExtraId),
  );

  const save = async () => {
    const deletions = changes
      .filter(([, quantity]) => quantity === 0)
      .flatMap(
        ([supplierExtraId]) => findBookingExtra(supplierExtraId)?.id ?? [],
      );
    const upserts = changes
      .filter(([, quantity]) => quantity > 0)
      .map(([supplierExtraId, quantity]) => ({
        id: findBookingExtra(supplierExtraId)?.id,
        supplier_purchasable_extra_id: supplierExtraId,
        quantity,
      }));

    await mutateAsync({
      id: booking.id,
      input: {
        purchasableExtras: { upsert: upserts, delete: deletions },
      },
    });

    setDraftQuantities({});
  };

  return (
    <div className="grid gap-3">
      <div className="font-semibold">{t("title")}</div>
      {extras.length > 0 ? (
        <dl className="grid gap-3">
          {extras.map((extra) => {
            const quantity =
              draftQuantities[extra.id] ?? savedQuantity(extra.id);
            const setQuantity = (value: number) =>
              setDraftQuantities((prev) => ({ ...prev, [extra.id]: value }));

            return (
              <div key={extra.id} className="flex items-center justify-between">
                <dt className={quantity > 0 ? undefined : "text-gray-500"}>
                  {extra.name}
                  <span className="ml-2 text-xs text-gray-400">
                    {formatCurrency(extra.unit_price, booking.currency)}
                  </span>
                </dt>
                <dd className="flex items-center gap-2">
                  <Button
                    outline
                    className="size-7"
                    aria-label={t("decrease", { name: extra.name })}
                    disabled={isPending || quantity === 0}
                    onClick={() => setQuantity(quantity - 1)}
                  >
                    <MinusIcon data-slot="icon" />
                  </Button>
                  <span
                    className={cn(
                      "w-6 text-center tabular-nums",
                      quantity !== savedQuantity(extra.id) &&
                        "font-semibold text-blue-600",
                    )}
                  >
                    {quantity}
                  </span>
                  <Button
                    outline
                    className="size-7"
                    aria-label={t("increase", { name: extra.name })}
                    disabled={isPending || quantity >= extra.units_purchasable}
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <PlusIcon data-slot="icon" />
                  </Button>
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
        <p className="text-gray-500">{t("noneOffered")}</p>
      )}
      {changes.length > 0 ? (
        <div className="flex justify-end gap-2">
          <Button
            plain
            disabled={isPending}
            onClick={() => setDraftQuantities({})}
          >
            {t("cancel")}
          </Button>
          <Button color="indigo" isLoading={isPending} onClick={save}>
            {t("save")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function InclusionIcon({ included }: { included: boolean | null | undefined }) {
  if (included === null || included === undefined) {
    return "--";
  }

  return included ? (
    <CheckIcon className="h-4 w-4 text-green-500" />
  ) : (
    <XMarkIcon className="h-4 w-4 text-red-500" />
  );
}
